<template>
    <div class="card" style="min-height: calc(100vh - 180px)">
        <div class="bg-blue-light p-1">
            <TitleButton btnTitle="Add Order" :showBtn="false" :showAddNew="true"
                :title="'Order Production Tracker'" @onClickAddNewButton="navigateToAddPage" />
        </div>

        <div class="px-2 pt-2 pb-1 section-tabs">
            <ul class="nav nav-tabs">
                <li v-for="tab in tabs" :key="tab.id" class="nav-item">
                    <a
                        :class="['nav-link', 'cursor-pointer', { active: activeTab.id === tab.id }]"
                        href="javascript:void(0)"
                        @click="setActiveTab(tab)"
                    >
                        {{ tab.title }}
                    </a>
                </li>
            </ul>
        </div>

        <div class="p-2 section-filters">
            <DateQuerySetter btnTitle="Go" @onClickGo="onClickGo">
                <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                    <label for="colFormLabel" class="col-form-label">Order No: </label>
                    <input class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
                        tabindex="1" type="text" placeholder="Please add LC number"
                        v-model="billNumber">
                </div>
                <div class="col-auto d-flex align-items-end mt-2">
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="showMoreFilters = !showMoreFilters">
                        {{ showMoreFilters ? 'Less' : 'More' }}
                    </button>
                </div>
            </DateQuerySetter>

            <div v-if="showMoreFilters" class="more-filters-card">
                <div class="row">
                    <div class="col-12 col-sm-6 col-md-4 col-lg-3 mt-tpp-1">
                        <div class="d-flex align-items-center justify-content-between filter-label">
                            <label for="colFormLabel" class="col-form-label">Customer Name: </label>
                            <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal('contactProfile')">
                                <i class="fas fa-search"></i>
                            </span>
                        </div>

                        <AsyncSelect placeholder="Select Customer Name" v-model="contactProfile"
                            :api-service="fetchContactProfiles" :additional-query="additionalContactOption"
                            :additional-option="additionalOption" :option-selected="onChangeContact"
                            :key="contactRenderKey" />
                    </div>
                    <div class="col-12 col-sm-6 col-md-4 col-lg-3 mt-tpp-1">
                        <div class="d-flex align-items-center justify-content-between filter-label">
                            <label for="colFormLabel" class="col-form-label">Buyer Name: </label>
                            <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal('buyerProfile')">
                                <i class="fas fa-search"></i>
                            </span>
                        </div>

                        <AsyncSelect placeholder="Select Buyer Name" v-model="buyerProfile"
                            :api-service="fetchContactProfiles" :additional-query="additionalBuyerOption"
                            :additional-option="additionalOption" :option-selected="onChangeContact"
                            :key="contactRenderKey" />
                    </div>
                    <div class="col-12 col-sm-6 col-md-4 col-lg-3 mt-tpp-1">

                        <div class="d-flex align-items-center justify-content-between filter-label">
                            <label for="colFormLabel" class="col-form-label">Sales Person: </label>
                            <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal('salesPersonProfile')">
                                <i class="fas fa-search"></i>
                            </span>
                        </div>

                        <AsyncSelect placeholder="Select Sales Person" v-model="salesPersonProfile"
                            :api-service="fetchContactProfiles" :additional-query="additionalSalesOption"
                            :additional-option="additionalOption" :option-selected="onChangeContact"
                            :key="contactRenderKey" />
                    </div>
                    <div class="col-12 col-sm-6 col-md-4 col-lg-3 mt-1">
                        <label class="form-label">Status</label>
                        <v-select
                            placeholder="Select Status"
                            v-model="selectedOrderStatus"
                            :options="orderStatuses"
                            label="name"
                            :reduce="option => option.value"
                        />
                    </div>
                    <div class="col-12 col-sm-6 col-md-4 col-lg-3 mt-1">
                        <label class="form-label">Invoice Status</label>
                        <v-select
                            placeholder="Select Invoice Status"
                            v-model="selectedInvoiceStatus"
                            :options="invoiceStatuses"
                            label="name"
                            :reduce="option => option.value"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="section-table pt-2">
            <div class="col-12">
              <div class="d-flex justify-content-end px-2">
                <button
                    @click="onClickToPrint"
                    class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect me-2"
                >
                  Print
                </button>
                <button
                    @click="onClickToExport"
                    class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect"
                >
                  Export
                </button>
              </div>
            </div>
            <div class="row summary-row mb-1 px-2">
                <div v-for="(tile, i) in kpiTiles" :key="i" class="col-md-2 col-sm-4 col-6 summary-row-item">
                    <div
                        class="stat-tile"
                        :class="[tile.borderClass, { 'stat-tile-clickable': tile.kpiFilter }]"
                        @click="onClickKpiTile(tile)"
                    >
                        <div class="stat-icon-badge" :class="tile.borderClass">
                            <i class="fas" :class="tile.icon"></i>
                        </div>
                        <p class="stat-label">{{ tile.name }}</p>
                        <h4 class="stat-value">{{ tile.value }}</h4>
                    </div>
                </div>
            </div>
            <div class="col-12 px-2 mt-2">
                <ListTable :purchase="purchaseList"  @orderStatusChanged="onClickGo" />
            </div>
            <div class="px-2">
                <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination" />
            </div>
        </div>

        <ContactDropdownFilter v-if="store.state.isModalOpenThree" @onSearchContact="onSearchContact"
            :companyRoles="companyRoles" :isFilterable="true" />
    </div>
</template>

<script setup>
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import { ref, inject, onMounted, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import handlePurchase from '@/services/modules/purchase'
import TitleButton from '@/components/atom/TitleButton'
import Pagination from '@/components/atom/Pagination'
import ListTable from '@/components/molecule/company/inventory/order-management/ProductionOrderTrackerListTable.vue'
import handleContact from "@/services/modules/contact";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleCrm from '@/services/modules/crm/crm'
import handleRole from "@/services/modules/role";
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import handleCompany from '@/services/modules/company'
import handleBarcode from '@/services/modules/barcode'
import productionOrderTrackerPdf from '@/services/utils/pdf/productionOrderTracker'
import figureFormatter from '@/services/utils/figureFormatter'

const router = useRouter()
const store = useStore()
const { commaFormat } = figureFormatter()
let saleType = ref('')
let offset = ref(20)
let purchaseList = ref([])
let contactProfile = ref(null)
let buyerProfile = ref(null)
let salesPersonProfile = ref(null)
const activeContactField = ref('contactProfile')
const listTotals = ref({})
const route = useRoute()
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const singleSR = ref(null);
const billNumber = ref('');
const singleDealer = ref(null);
const dealerRenderKey = ref(0);
const contactRenderKey = ref(0);

const { fetchSalesOrderList, fetchOrderStatus } = handlePurchase()
const { fetchContactProfiles } = handleContact();
const { fetchCompanyDefaultRoles } = handleRole();
const { fetchAllDealers, fetchAllSR } = handleCrm();
const { fetchCompanyInfo } = handleCompany();
const { fetchBarcode } = handleBarcode();
const { exportToPDF } = productionOrderTrackerPdf();
const companyInfo = ref(null);
const additionalOption = ref([]);
const additionalDealerQuery = ref({});
const additionalContactOption = ref({
    with_mobile: 1,
    dealer_id: '',
    roles: 'customer,lead',
    pi_order_no: null
});
const additionalBuyerOption = ref({
    with_mobile: 1,
    dealer_id: '',
    roles: 'buyer'
});
const additionalSalesOption = ref({
    with_mobile: 1,
    dealer_id: '',
    roles: 'sales_representative'
});
const companyRoles = ref(null);
const additionalDealerOption = ref([]);
const invoiceStatuses = ref([]);
const orderStatuses = ref([]);
const selectedInvoiceStatus = ref(null);
const selectedOrderStatus = ref(null);
const showMoreFilters = ref(false);
const tabs = [
    { id: 'running', title: 'Running Order' },
    { id: 'new', title: 'New Order' },
    { id: 'completed', title: 'Completed' }
]
const activeTab = ref(tabs[0]);

const onChangeContact = (e) => {
    //TODO: contact profile id
}

const onClickToExport = () => {
  let token = localStorage.getItem('token');
  let url = `${process.env.VUE_APP_BASE_URL}/api/inventory/sales/order${getQuery()}&_token=${token}&is_export=1&is_order_production_tracker=1`;
  let a = document.createElement("a")
  a.setAttribute("href", url)
  a.click()
}

const getCompany = async () => {
    const res = await fetchCompanyInfo(companyId.value)
    if (res.data) companyInfo.value = res.data
}

const onClickToPrint = async () => {
    const userName = store.state.user.user.name
    const res = await fetchSalesOrderList(getQuery() + '&is_all=1')
    if (!res.status) return
    const qrcodeData = JSON.stringify({ created_by: userName, created_date_time: new Date().toISOString() })
    const barcodeRes = await fetchBarcode(`?code=production-order-tracker&qrcode=${qrcodeData}`)
    exportToPDF(companyInfo.value, res.date, activeTab.value.title, userName, barcodeRes.qrcode, res.totals)
}

const getCompanyRoles = async () => {
    const res = await fetchCompanyDefaultRoles(`?company_id=${companyId.value}&roles=customer,lead`);
    if (res.status) {
        companyRoles.value = res.data
    }
}

const onOpenContactSearchModal = (field = 'contactProfile') => {
    activeContactField.value = field
    store.state.isModalOpenThree = true;
}

const contactFieldRefs = {
    contactProfile: contactProfile,
    buyerProfile: buyerProfile,
    salesPersonProfile: salesPersonProfile
}

const onSearchContact = (contact) => {
    contactFieldRefs[activeContactField.value].value = contact
}

const companyId = computed(() => {
    return route.params.companyId
})
const startDate = computed(() => {
    return route.query.start
})
const endDate = computed(() => {
    return route.query.end
})
const page = computed(() => {
    return route.query.page
})
const kpiFilter = computed(() => {
    return route.query.kpi_filter
})
const fromApproval = computed(() => {
    return route.name === 'sales-order-approval';
})

watch(() => singleSR.value, (newValue, oldValue) => {
    additionalDealerQuery.value = {};
    additionalContactOption.value.dealer_id = '';
    singleDealer.value = null;
    contactProfile.value = null;
    dealerRenderKey.value += 1;
    contactRenderKey.value += 1;

    if (newValue) {
        additionalDealerQuery.value = { 'sale_representative_id': newValue.id }
    }
})

watch(() => singleDealer.value, (newValue, oldValue) => {
    additionalContactOption.value.dealer_id = '';
    contactProfile.value = null;
    contactRenderKey.value += 1;

    if (newValue) {
        additionalContactOption.value.dealer_id = newValue.id
    }
})

function navigateToAddPage() {
    const query = Object.assign({}, route.query)
    query.type = 'order_production_tracker';

    router.push({ name: 'product-sales-order', params: route.params, query: query })
}

function getQuery() {
    let query = '?company_id=' + companyId.value
    query += '&tab=' + activeTab.value.id
    query += '&is_multi_invoice=1'
    query += '&with_totals=1'
    if (kpiFilter.value) query += '&kpi_filter=' + kpiFilter.value
    if (!startDate.value && !endDate.value) return query
    if (contactProfile.value) query += '&contact_profile_id=' + contactProfile.value.id
    if (buyerProfile.value) query += '&buyer_id=' + buyerProfile.value.id
    if (salesPersonProfile.value) query += '&sales_person_id=' + salesPersonProfile.value.id
    if (startDate.value) query += '&start_date=' + startDate.value
    if (endDate.value) query += '&end_date=' + endDate.value
    if (page.value) query += '&page=' + page.value
    if(billNumber.value) query += '&bill_number='+billNumber.value
    if (selectedOrderStatus.value) query += '&status=' + selectedOrderStatus.value
    if (selectedInvoiceStatus.value) query += '&invoice_status=' + selectedInvoiceStatus.value
    query += '&sale_type=' + saleType.value
    query += '&offset=' + offset.value
    return query
}

function setActiveTab(tab) {
    activeTab.value = tab
    const routeQuery = Object.assign({}, route.query, { tab: tab.id })
    delete routeQuery.page
    router.replace({ path: route.path, query: routeQuery })
    onClickGo()
}

function restoreActiveTab() {
    const currentTab = route.query.tab
    if (!currentTab) return
    const matched = tabs.find(t => t.id === currentTab)
    if (!matched) return
    activeTab.value = matched
}

function onClickGo() {
    fetchSalesOrderList(getQuery())
        .then(res => {
            if (!res.status) {
              purchaseList.value = []
              listTotals.value = {}
              store.commit("resetPagination")
              return
            }
          purchaseList.value = res.date.data
          listTotals.value = res.totals || {}
          setPagination(res.date)
        })
}

const getOrderStatus = async () => {
    const res = await fetchOrderStatus('?company_id=' + companyId.value);
    if (res.status) {
        invoiceStatuses.value = res.data.filter(item => ['created', 'pending'].includes(item.value))
        orderStatuses.value = res.data.filter(item => ['pending', 'approved', 'cancelled'].includes(item.value))
    }
}

function setPagination(data) {
    if (!data.data.length) return
    store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

function onPageChange(page) {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    router.push({ path: route.path, query: routeQuery })
}

const numberOrZero = (value) => {
    if (!value) return 0
    return value
}

const kpiTiles = computed(() => {
    return [
        { name: 'Order Qty', value: numberOrZero(listTotals.value.total_pi_quantity), borderClass: 'border-opening', icon: 'fa-box' },
        { name: 'Production Qty', value: numberOrZero(listTotals.value.total_production_quantity), borderClass: 'border-debit', icon: 'fa-industry' },
        { name: 'Delivered Qty', value: numberOrZero(listTotals.value.total_delivered_quantity), borderClass: 'border-credit', icon: 'fa-truck' },
        { name: 'Pending Delivery Qty', value: numberOrZero(listTotals.value.pending_delivery_quantity), borderClass: 'border-closing', icon: 'fa-hourglass-half', kpiFilter: 'pending_delivery' },
        { name: 'Ready for Delivery Qty', value: numberOrZero(listTotals.value.ready_for_delivery_quantity), borderClass: 'border-ready', icon: 'fa-dolly', kpiFilter: 'ready_for_delivery' },
        { name: 'Pending Production Qty', value: numberOrZero(listTotals.value.pending_production_quantity), borderClass: 'border-pending', icon: 'fa-cogs', kpiFilter: 'pending_production' },
        { name: 'Order Value', value: commaFormat(listTotals.value.total_amount), borderClass: 'border-opening', icon: 'fa-money-bill-wave' },
        { name: 'Delivery Value', value: commaFormat(listTotals.value.total_delivery_amount), borderClass: 'border-debit', icon: 'fa-truck-loading' },
        { name: 'Paid Amount', value: commaFormat(listTotals.value.paid_amount), borderClass: 'border-credit', icon: 'fa-hand-holding-usd' },
        { name: 'Due Amount', value: commaFormat(listTotals.value.due_amount), borderClass: 'border-closing', icon: 'fa-exclamation-circle', kpiFilter: 'due_amount' }
    ]
})

function onClickKpiTile(tile) {
    if (! tile.kpiFilter) return
    const routeData = router.resolve({
        name: 'order-production-tracker',
        params: route.params,
        query: Object.assign({}, route.query, { tab: activeTab.value.id, kpi_filter: tile.kpiFilter })
    })
    window.open(routeData.href, '_blank')
}

onMounted(() => {
    restoreActiveTab()
    onClickGo()
    getOrderStatus()
    getCompanyRoles()
    getCompany()
})

</script>

<style scoped>
.mt-tpp-1 {
    margin-top: 1%;
}

.filter-label {
    padding-right: 0.5rem !important;
}

.nav-tabs .nav-link {
    cursor: pointer;
}

.section-tabs {
    background: #EDF1F7;
    border-radius: 6px 6px 0 0;
    margin-bottom: -1px;
}

.section-filters {
    background: #F4F5F7;
    margin-top: -1px;
    margin-bottom: -1px;
}

.section-table {
    background: #FFFFFF;
    border-radius: 0 0 6px 6px;
}

.summary-row-item {
    padding: 5px;
}

.stat-tile {
    position: relative;
    height: 100%;
    min-height: 92px;
    padding: 14px 50px 14px 14px;
    border: 1px solid #d8d6e0;
    border-radius: 10px;
    background: linear-gradient(180deg, #fbfbfd 0%, #ffffff 100%);
    box-shadow: 0 2px 10px rgba(34, 41, 47, 0.08);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-tile:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 18px rgba(34, 41, 47, 0.14);
}

.stat-tile-clickable {
    cursor: pointer;
}

.stat-icon-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    background-color: rgba(115, 103, 240, 0.12);
    color: #7367f0;
}

.stat-icon-badge.border-opening {
    background-color: rgba(0, 207, 232, 0.12);
    color: #00cfe8;
}

.stat-icon-badge.border-debit {
    background-color: rgba(115, 103, 240, 0.12);
    color: #7367f0;
}

.stat-icon-badge.border-credit {
    background-color: rgba(40, 199, 111, 0.12);
    color: #28c76f;
}

.stat-icon-badge.border-closing {
    background-color: rgba(234, 84, 85, 0.12);
    color: #ea5455;
}

.stat-icon-badge.border-ready {
    background-color: rgba(255, 159, 67, 0.12);
    color: #ff9f43;
}

.stat-icon-badge.border-pending {
    background-color: rgba(130, 134, 139, 0.12);
    color: #82868b;
}

.stat-label {
    margin-bottom: 6px;
    font-size: 0.92rem;
    font-weight: 600;
    color: #000000;
    letter-spacing: 0.2px;
}

.stat-value {
    margin-bottom: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: #2f2b3d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.border-opening {
    border-color: #00cfe8;
}

.border-debit {
    border-color: #7367f0;
}

.border-credit {
    border-color: #28c76f;
}

.border-closing {
    border-color: #ea5455;
}

@media (max-width: 576px) {
    .stat-value {
        font-size: 1.3rem;
    }
    .stat-tile {
        min-height: 80px;
    }
}

</style>
